<template>
  <div
    v-if="isMobileAppPromoLayout"
    class="mobile-app-promo"
  >
    <div class="mobile-app-promo__text">
      Экскурсии, отзывы и&nbsp;чат с&nbsp;гидом&nbsp;&mdash;
      <br class="mobile-app-promo__text-break" />
      в&nbsp;приложении
    </div>

    <div class="mobile-app-promo__items">
      <div class="mobile-app-promo__item mobile-app-promo__qr-code">
        <IconBase name="qr-code-in-article" />
      </div>

      <a
        :href="appLinksUrls.google"
        rel="noreferrer nofollow"
        target="_blank"
        class="mobile-app-promo__item mobile-app-promo-item__google-play"
        @click.prevent="handleClick('google')"
      >
        <div class="mobile-app-promo-icon-wrapper">
          <IconBase
            class="mobile-app-promo-icon"
            name="google-play"
          />
        </div>

        <div class="mobile-app-promo-text">Доступно в&nbsp;Google&nbsp;Play</div>
      </a>

      <a
        :href="appLinksUrls.apple"
        rel="noreferrer nofollow"
        target="_blank"
        class="mobile-app-promo__item mobile-app-promo-item__app-store"
        @click.prevent="handleClick('apple')"
      >
        <div class="mobile-app-promo-icon-wrapper">
          <IconBase
            class="mobile-app-promo-icon"
            name="apple"
          />
        </div>

        <div class="mobile-app-promo-text">Загрузить в&nbsp;App&nbsp;Store</div>
      </a>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { buildTravelersAppLinksUrl, SITE_ONELINK_SOURCE } from '@experience/onelink-lib';
  import { computed } from 'vue';

  import IconBase from '@/shared/ui/iconBase/IconBase.vue';

  type Store = 'google' | 'apple';

  const props = withDefaults(
    defineProps<{
      kind?: 'usp' | 'mobile-app-promo';
    }>(),
    {
      kind: 'mobile-app-promo',
    },
  );

  const isMobileAppPromoLayout = computed(() => {
    return props.kind !== 'usp';
  });

  // Desktop и mobile — через MFE travelers-app-links (Onelink v4):
  // атрибуция, SOGU redirect:website-to-app и store=google|apple живут в МФ.
  const buildStoreAppLinksUrl = (store: Store) => {
    const params = new URLSearchParams();
    params.set('store', store);

    return buildTravelersAppLinksUrl(SITE_ONELINK_SOURCE.PROMO_JOURNAL_BANNER, {
      search: `?${params.toString()}`,
    });
  };

  const appLinksUrls = computed<Record<Store, string>>(() => ({
    google: buildStoreAppLinksUrl('google'),
    apple: buildStoreAppLinksUrl('apple'),
  }));

  const handleClick = (store: Store) => {
    window.open(appLinksUrls.value[store], '_blank', 'noopener,noreferrer');
  };
</script>

<style lang="scss" scoped>
  .mobile-app-promo {
    display: flex;
    gap: 24px;
    box-sizing: border-box;
    width: 100%;
    max-width: var(--width-content);
    margin-top: 48px;
    padding: 24px 24px 24px 48px;
    border-radius: var(--alias-radius-32, 32px);
    background-color: var(--alias-color-background-accent-green-lightest);
    color: var(--alias-color-default);
    text-decoration: none;

    @include tablet {
      margin-top: 32px;
    }

    @include mobile {
      display: grid;
      grid-template-columns: 1fr;
      justify-items: center;
      padding: 24px;
    }
  }

  .mobile-app-promo__text {
    display: flex;
    flex-grow: 1;
    flex-shrink: 1;
    justify-content: stretch;
    align-items: center;

    @include base-700-500-semi;

    @include mobile {
      grid-row: 1;
      justify-content: center;
      text-align: center;
    }
  }

  .mobile-app-promo__text-break {
    @include mobile {
      display: none;
    }
  }

  .mobile-app-promo__items {
    display: flex;
    gap: 6px;
  }

  .mobile-app-promo__item {
    display: flex;
    justify-content: stretch;
    align-items: stretch;
    box-sizing: border-box;
    width: 116px;
    height: 116px;
    padding: 16px;
    border-radius: var(--alias-radius-24, 24px);
    background-color: var(--global-color-transparent-black-a050);
    text-decoration: none;
  }

  .mobile-app-promo-icon-wrapper {
    display: flex;
    justify-content: flex-end;
    width: 100%;
  }

  .mobile-app-promo-icon {
    flex-shrink: 0;
    width: 33px;
    height: 44px;
    fill: var(--alias-color-default);
    aspect-ratio: 3/4;
  }

  .mobile-app-promo-text {
    color: var(--alias-color-text-default);

    @include base-200-200-medium;
  }

  .mobile-app-promo__qr-code {
    @include mobile {
      display: none;
    }
  }

  .mobile-app-promo-item__google-play,
  .mobile-app-promo-item__app-store {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding-top: 10px;
    cursor: pointer;

    &:hover {
      background-color: var(--global-color-transparent-black-a100);
    }
  }
</style>
